/**
 * WebM Writer/Muxer
 *
 * Muxes VP9 video and Opus audio into WebM container format
 * Based on the Matroska/EBML specification
 */

interface WebMWriterConfig {
  width: number;
  height: number;
  fps: number;
  hasAudio: boolean;
  audioSampleRate: number;
}

interface VideoChunkData {
  chunk: EncodedVideoChunk;
  metadata?: EncodedVideoChunkMetadata;
}

interface AudioChunkData {
  // FIX: Cannot find name 'EncodedAudioChunk'. Use `any` as a workaround for missing WebCodecs types.
  chunk: any;
  // FIX: Cannot find name 'EncodedAudioChunkMetadata'. Use `any` as a workaround for missing WebCodecs types.
  metadata?: any;
}

/**
 * EBML (Extensible Binary Meta Language) utilities for WebM/Matroska
 */
class EBMLWriter {
  private data: Uint8Array[] = [];

  /**
   * Write EBML element
   */
  writeElement(id: number, data: Uint8Array | number | string): void {
    // Write element ID
    this.writeID(id);

    // Convert data to Uint8Array if needed
    let dataBytes: Uint8Array;
    if (typeof data === 'number') {
      // Integer
      dataBytes = this.numberToBytes(data);
    } else if (typeof data === 'string') {
      // String
      const encoder = new TextEncoder();
      dataBytes = encoder.encode(data);
    } else {
      dataBytes = data;
    }

    // Write data size
    this.writeVInt(dataBytes.length);

    // Write data
    this.data.push(dataBytes);
  }

  /**
   * Write variable-length integer (vint)
   */
  private writeVInt(value: number): void {
    // Determine number of bytes needed
    let bytesNeeded = 1;
    let maxValue = (1 << (7 * bytesNeeded)) - 2;

    while (value > maxValue && bytesNeeded < 8) {
      bytesNeeded++;
      maxValue = (1 << (7 * bytesNeeded)) - 2;
    }

    // Create vint
    const bytes = new Uint8Array(bytesNeeded);
    let remaining = value;

    for (let i = bytesNeeded - 1; i > 0; i--) {
      bytes[i] = remaining & 0xFF;
      remaining >>= 8;
    }

    // Set marker bit
    bytes[0] = (1 << (8 - bytesNeeded)) | remaining;

    this.data.push(bytes);
  }

  /**
   * Write element ID
   *
   * EBML element IDs are encoded as big-endian integers with a length between
   * 1 and 4 bytes depending on the magnitude of the identifier. Unlike the
   * variable-length integers used for sizes, the element ID must preserve its
   * raw byte pattern. Using the vint encoding here (which adds a marker bit to
   * indicate length) produces invalid IDs and results in WebM files where only
   * the first frame is displayed. This method encodes the ID using the minimal
   * number of bytes required while keeping the original bit pattern intact.
   */
  private writeID(id: number): void {
    const bytes: number[] = [];

    if (id >= 0x01000000) {
      bytes.push((id >> 24) & 0xFF);
    }
    if (id >= 0x00010000) {
      bytes.push((id >> 16) & 0xFF);
    }
    if (id >= 0x00000100) {
      bytes.push((id >> 8) & 0xFF);
    }

    bytes.push(id & 0xFF);

    this.data.push(new Uint8Array(bytes));
  }

  /**
   * Convert number to bytes
   */
  private numberToBytes(value: number): Uint8Array {
    if (value < 256) {
      return new Uint8Array([value]);
    } else if (value < 65536) {
      return new Uint8Array([value >> 8, value & 0xFF]);
    } else if (value < 16777216) {
      return new Uint8Array([value >> 16, (value >> 8) & 0xFF, value & 0xFF]);
    } else {
      return new Uint8Array([
        value >> 24,
        (value >> 16) & 0xFF,
        (value >> 8) & 0xFF,
        value & 0xFF
      ]);
    }
  }

  /**
   * Write float (64-bit)
   */
  writeFloat(id: number, value: number): void {
    const buffer = new ArrayBuffer(8);
    const view = new DataView(buffer);
    view.setFloat64(0, value, false); // big-endian
    this.writeElement(id, new Uint8Array(buffer));
  }

  /**
   * Get all data
   */
  getData(): Uint8Array {
    const totalLength = this.data.reduce((sum, arr) => sum + arr.length, 0);
    const result = new Uint8Array(totalLength);
    let offset = 0;

    for (const arr of this.data) {
      result.set(arr, offset);
      offset += arr.length;
    }

    return result;
  }

  /**
   * Get data length
   */
  getLength(): number {
    return this.data.reduce((sum, arr) => sum + arr.length, 0);
  }

  /**
   * Append another writer's data
   */
  append(other: EBMLWriter): void {
    this.data.push(...other.data);
  }
}

/**
 * WebM Writer class
 */
export class WebMWriter {
  private config: WebMWriterConfig;
  private videoChunks: VideoChunkData[] = [];
  private audioChunks: AudioChunkData[] = [];
  private videoTrackNumber = 1;
  private audioTrackNumber = 2;
  private duration = 0;
  private clusterTimestamp = 0;
  private clusters: EBMLWriter[] = [];

  // EBML IDs (Matroska/WebM)
  private readonly IDs = {
    EBML: 0x1A45DFA3,
    EBMLVersion: 0x4286,
    EBMLReadVersion: 0x42F7,
    EBMLMaxIDLength: 0x42F2,
    EBMLMaxSizeLength: 0x42F3,
    DocType: 0x4282,
    DocTypeVersion: 0x4287,
    DocTypeReadVersion: 0x4285,

    Segment: 0x18538067,
    Info: 0x1549A966,
    TimecodeScale: 0x2AD7B1,
    MuxingApp: 0x4D80,
    WritingApp: 0x5741,
    Duration: 0x4489,

    Tracks: 0x1654AE6B,
    TrackEntry: 0xAE,
    TrackNumber: 0xD7,
    TrackUID: 0x73C5,
    TrackType: 0x83,
    FlagLacing: 0x9C,
    CodecID: 0x86,
    CodecPrivate: 0x63A2,

    Video: 0xE0,
    PixelWidth: 0xB0,
    PixelHeight: 0xBA,

    Audio: 0xE1,
    SamplingFrequency: 0xB5,
    Channels: 0x9F,
    BitDepth: 0x6264,

    Cluster: 0x1F43B675,
    Timecode: 0xE7,
    SimpleBlock: 0xA3,
  };

  constructor(config: WebMWriterConfig) {
    this.config = config;
  }

  /**
   * Add video chunk
   */
  addVideoChunk(chunk: EncodedVideoChunk, metadata?: EncodedVideoChunkMetadata): void {
    this.videoChunks.push({ chunk, metadata });

    // Update duration
    const chunkEndTime = (chunk.timestamp + (chunk.duration || 0)) / 1000000; // Convert to seconds
    if (chunkEndTime > this.duration) {
      this.duration = chunkEndTime;
    }
  }

  /**
   * Add audio chunk
   */
  // FIX: Cannot find name 'EncodedAudioChunk' and 'EncodedAudioChunkMetadata'. Use `any` for missing WebCodecs types.
  addAudioChunk(chunk: any, metadata?: any): void {
    this.audioChunks.push({ chunk, metadata });

    // Update duration
    const chunkEndTime = (chunk.timestamp + (chunk.duration || 0)) / 1000000; // Convert to seconds
    if (chunkEndTime > this.duration) {
      this.duration = chunkEndTime;
    }
  }

  /**
   * Complete and create WebM blob
   */
  async complete(): Promise<Blob> {
    const ebml = new EBMLWriter();

    // Write EBML header
    this.writeEBMLHeader(ebml);

    // Write Segment
    const segment = new EBMLWriter();

    // Write Info
    this.writeInfo(segment);

    // Write Tracks
    this.writeTracks(segment);

    // Write Clusters
    await this.writeClusters(segment);

    // Wrap segment
    const segmentData = segment.getData();
    ebml.writeElement(this.IDs.Segment, segmentData);

    // Create blob
    const data = ebml.getData();
    return new Blob([data], { type: 'video/webm' });
  }

  /**
   * Write EBML header
   */
  private writeEBMLHeader(ebml: EBMLWriter): void {
    const header = new EBMLWriter();
    header.writeElement(this.IDs.EBMLVersion, 1);
    header.writeElement(this.IDs.EBMLReadVersion, 1);
    header.writeElement(this.IDs.EBMLMaxIDLength, 4);
    header.writeElement(this.IDs.EBMLMaxSizeLength, 8);
    header.writeElement(this.IDs.DocType, 'webm');
    header.writeElement(this.IDs.DocTypeVersion, 2);
    header.writeElement(this.IDs.DocTypeReadVersion, 2);

    const headerData = header.getData();
    ebml.writeElement(this.IDs.EBML, headerData);
  }

  /**
   * Write Info section
   */
  private writeInfo(segment: EBMLWriter): void {
    const info = new EBMLWriter();
    info.writeElement(this.IDs.TimecodeScale, 1000000); // 1ms
    info.writeElement(this.IDs.MuxingApp, 'LVMH Video Editor');
    info.writeElement(this.IDs.WritingApp, 'WebCodecs Exporter');
    info.writeFloat(this.IDs.Duration, this.duration * 1000); // in timecode scale units

    const infoData = info.getData();
    segment.writeElement(this.IDs.Info, infoData);
  }

  /**
   * Write Tracks section
   */
  private writeTracks(segment: EBMLWriter): void {
    const tracks = new EBMLWriter();

    // Video track
    const videoTrack = new EBMLWriter();
    videoTrack.writeElement(this.IDs.TrackNumber, this.videoTrackNumber);
    videoTrack.writeElement(this.IDs.TrackUID, this.videoTrackNumber);
    videoTrack.writeElement(this.IDs.TrackType, 1); // Video
    videoTrack.writeElement(this.IDs.FlagLacing, 0);
    videoTrack.writeElement(this.IDs.CodecID, 'V_VP9');

    // Video settings
    const videoSettings = new EBMLWriter();
    videoSettings.writeElement(this.IDs.PixelWidth, this.config.width);
    videoSettings.writeElement(this.IDs.PixelHeight, this.config.height);
    const videoSettingsData = videoSettings.getData();
    videoTrack.writeElement(this.IDs.Video, videoSettingsData);

    // Add codec private data if available
// FIX: Correctly handle AllowSharedBufferSource type for Uint8Array constructor by casting to any.
    if (this.videoChunks.length > 0 && (this.videoChunks[0].metadata as any)?.decoderConfig?.description) {
      const description = (this.videoChunks[0].metadata as any).decoderConfig.description;
      const data = (description instanceof ArrayBuffer) ?
        new Uint8Array(description) :
        new Uint8Array((description as any).buffer, (description as any).byteOffset, (description as any).byteLength);
      videoTrack.writeElement(
        this.IDs.CodecPrivate,
        data
      );
    }

    const videoTrackData = videoTrack.getData();
    tracks.writeElement(this.IDs.TrackEntry, videoTrackData);

    // Audio track
    if (this.config.hasAudio) {
      const audioTrack = new EBMLWriter();
      audioTrack.writeElement(this.IDs.TrackNumber, this.audioTrackNumber);
      audioTrack.writeElement(this.IDs.TrackUID, this.audioTrackNumber);
      audioTrack.writeElement(this.IDs.TrackType, 2); // Audio
      audioTrack.writeElement(this.IDs.FlagLacing, 0);
      audioTrack.writeElement(this.IDs.CodecID, 'A_OPUS');

      // Audio settings
      const audioSettings = new EBMLWriter();
      audioSettings.writeFloat(this.IDs.SamplingFrequency, this.config.audioSampleRate);
      audioSettings.writeElement(this.IDs.Channels, 2);
      const audioSettingsData = audioSettings.getData();
      audioTrack.writeElement(this.IDs.Audio, audioSettingsData);

      // Add codec private data if available
// FIX: Correctly handle AllowSharedBufferSource type for Uint8Array constructor by casting to any.
      if (this.audioChunks.length > 0 && (this.audioChunks[0].metadata as any)?.decoderConfig?.description) {
        const description = (this.audioChunks[0].metadata as any).decoderConfig.description;
        const data = (description instanceof ArrayBuffer) ?
          new Uint8Array(description) :
          new Uint8Array((description as any).buffer, (description as any).byteOffset, (description as any).byteLength);
        audioTrack.writeElement(
          this.IDs.CodecPrivate,
          data
        );
      }

      const audioTrackData = audioTrack.getData();
      tracks.writeElement(this.IDs.TrackEntry, audioTrackData);
    }

    const tracksData = tracks.getData();
    segment.writeElement(this.IDs.Tracks, tracksData);
  }

  /**
   * Write Clusters
   */
  private async writeClusters(segment: EBMLWriter): Promise<void> {
    // Combine and sort all chunks by timestamp
    interface CombinedChunk {
      type: 'video' | 'audio';
      timestamp: number;
      duration: number;
      data: Uint8Array;
      isKeyframe: boolean;
      trackNumber: number;
    }

    const allChunks: CombinedChunk[] = [];

    // Add video chunks
    for (const { chunk } of this.videoChunks) {
      const data = new Uint8Array(chunk.byteLength);
      chunk.copyTo(data);

      allChunks.push({
        type: 'video',
        timestamp: chunk.timestamp,
        duration: chunk.duration || 0,
        data,
        isKeyframe: chunk.type === 'key',
        trackNumber: this.videoTrackNumber,
      });
    }

    // Add audio chunks
    for (const { chunk } of this.audioChunks) {
      const data = new Uint8Array(chunk.byteLength);
      chunk.copyTo(data);

      allChunks.push({
        type: 'audio',
        timestamp: chunk.timestamp,
        duration: chunk.duration || 0,
        data,
        isKeyframe: chunk.type === 'key',
        trackNumber: this.audioTrackNumber,
      });
    }

    // Sort by timestamp
    allChunks.sort((a, b) => a.timestamp - b.timestamp);

    // Create clusters (group chunks into ~1 second clusters)
    const clusterDuration = 1000000; // 1 second in microseconds
    let currentCluster = new EBMLWriter();
    let currentClusterTimestamp = 0;
    let clusterStarted = false;

    for (const chunk of allChunks) {
      // Start new cluster if needed
      if (!clusterStarted || chunk.timestamp - currentClusterTimestamp >= clusterDuration) {
        if (clusterStarted) {
          // Save previous cluster
          const clusterData = currentCluster.getData();
          segment.writeElement(this.IDs.Cluster, clusterData);
        }

        // Start new cluster
        currentCluster = new EBMLWriter();
        currentClusterTimestamp = chunk.timestamp;
        currentCluster.writeElement(this.IDs.Timecode, Math.round(currentClusterTimestamp / 1000));
        clusterStarted = true;
      }

      // Write SimpleBlock
      this.writeSimpleBlock(currentCluster, chunk, currentClusterTimestamp);
    }

    // Save last cluster
    if (clusterStarted) {
      const clusterData = currentCluster.getData();
      segment.writeElement(this.IDs.Cluster, clusterData);
    }
  }

  /**
   * Write SimpleBlock
   */
  private writeSimpleBlock(
    cluster: EBMLWriter,
    chunk: { type: string; timestamp: number; data: Uint8Array; isKeyframe: boolean; trackNumber: number },
    clusterTimestamp: number
  ): void {
    // SimpleBlock format:
    // Track number (vint)
    // Timecode (2 bytes, signed int16, relative to cluster)
    // Flags (1 byte)
    // Data

    const relativeTimecode = Math.round((chunk.timestamp - clusterTimestamp) / 1000);

    // Create block data
    const blockData = new Uint8Array(4 + chunk.data.length);

    // Track number (1 byte for tracks 1-127)
    blockData[0] = 0x80 | chunk.trackNumber;

    // Timecode (2 bytes, big-endian signed int16)
    blockData[1] = (relativeTimecode >> 8) & 0xFF;
    blockData[2] = relativeTimecode & 0xFF;

    // Flags
    let flags = 0;
    if (chunk.isKeyframe) {
      flags |= 0x80; // Keyframe flag
    }
    blockData[3] = flags;

    // Data
    blockData.set(chunk.data, 4);

    cluster.writeElement(this.IDs.SimpleBlock, blockData);
  }
}